/**
 * Stormware POHODA XML (schema version 2) for one company's month: received documents
 * (agenda Přijaté faktury, `inv:invoice` with `receivedInvoice`) and bank movements
 * (agenda Banka, `bnk:bank`). Pure builders, no I/O: `pohodaExport.ts` loads the data,
 * fetches the files and zips everything; the pohoda-send edge function sends the same
 * dataPack to mServer. Pure (no Deno or browser APIs beyond TextDecoder), so the
 * browser imports it through src/lib/exports/pohoda.ts.
 *
 * Schemas and the reasoning behind every choice here: docs/exports/POHODA.md.
 */

export const POHODA_NS = {
  dat: "http://www.stormware.cz/schema/version_2/data.xsd",
  inv: "http://www.stormware.cz/schema/version_2/invoice.xsd",
  bnk: "http://www.stormware.cz/schema/version_2/bank.xsd",
  typ: "http://www.stormware.cz/schema/version_2/type.xsd",
} as const;

export const LOCAL_CURRENCY = "CZK";
const APPLICATION = "Zettl";

export interface PohodaSupplier {
  name: string | null;
  ico?: string | null;
  dic?: string | null;
  street?: string | null;
  city?: string | null;
  zip?: string | null;
}

/** One VAT rate's share of a document, in the document currency. */
export interface PohodaVatLine {
  /** Percent: 21, 12, 0 … */
  rate: number;
  base: number;
  vat: number;
}

/** A receipt or received invoice as Zettl holds it. */
export interface PohodaReceivedDocument {
  /** Zettl row id; the Pohoda number and the import id derive from it. */
  id: string;
  /** The supplier's own invoice number. */
  documentNumber?: string | null;
  supplier?: PohodaSupplier | null;
  issueDate: string | null;
  taxDate?: string | null;
  dueDate?: string | null;
  currency?: string | null;
  /** CZK for one unit of `currency`; null lets Pohoda use its own rate list. */
  rate?: number | null;
  /** Total including VAT, in the document currency. */
  total: number | null;
  /** VAT breakdown when it was extracted; without it the total goes in as "bez DPH". */
  vat?: PohodaVatLine[] | null;
  variableSymbol?: string | null;
  description?: string | null;
  /** Path of the attached file inside the ZIP, e.g. `doklady/Z3F2A9C01B7D4.pdf`. */
  attachmentPath?: string | null;
}

export interface PohodaBankMovement {
  id: string;
  date: string;
  /** Signed: negative is money out. */
  amount: number;
  currency?: string | null;
  counterAccount?: string | null;
  counterBankCode?: string | null;
  counterName?: string | null;
  variableSymbol?: string | null;
  constantSymbol?: string | null;
  specificSymbol?: string | null;
  description?: string | null;
  /** Zettl ids of the documents this payment is matched to. */
  matchedDocumentIds?: string[];
}

export interface PohodaPackHeader {
  /** IČO of the company the data is for (organizations.ico). */
  ico: string;
  /** `YYYY-MM`. */
  period: string;
  /** Stable per company, so two companies' months never share a dataPack id. */
  organizationId: string;
}

// ---------------------------------------------------------------------------
// Text and numbers
// ---------------------------------------------------------------------------

export function escapeXml(value: string): string {
  return (
    value
      // Characters XML 1.0 does not allow (control characters except tab/LF/CR).
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]/g, "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&apos;")
  );
}

/** Cut to the schema's maxLength (counted in characters), whitespace collapsed. */
function clip(value: string | null | undefined, max: number): string {
  const s = (value ?? "").replace(/\s+/g, " ").trim();
  return Array.from(s).slice(0, max).join("");
}

/** Amount for the XML: dot decimal, two places, never exponent form. */
export function pohodaAmount(n: number): string {
  const rounded = Math.round((n + Number.EPSILON) * 100) / 100;
  return (Object.is(rounded, -0) ? 0 : rounded).toFixed(2);
}

/** Exchange rate: up to three decimals as ČNB publishes it, trailing zeros trimmed. */
function rateText(n: number): string {
  return n.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
}

/** `2026-10-05` from an ISO date or timestamp; null when it is not a date. */
export function isoDate(value: string | null | undefined): string | null {
  const m = (value ?? "").match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
}

/** Digits only, as Czech payment symbols are; null when nothing is left. */
function symbol(value: string | null | undefined, max: number): string | null {
  const digits = (value ?? "").replace(/\D/g, "");
  if (!digits) return null;
  return digits.length <= max ? digits : null;
}

const el = (name: string, value: string | null | undefined) =>
  value == null || value === "" ? "" : `<${name}>${escapeXml(value)}</${name}>`;

// ---------------------------------------------------------------------------
// Identity: stable numbers so a repeated import is recognised
// ---------------------------------------------------------------------------

/**
 * The document number in Pohoda (`numberRequested`, max 32): `Z` + the first twelve
 * hex digits of the Zettl id. Same receipt, same number, every export; Pohoda refuses
 * a second document with that number (checkDuplicity defaults to true). Also used as
 * the pairing symbol (symPar, max 20) on the document and the payment that settles it.
 */
export function pohodaDocumentNumber(id: string): string {
  return `Z${id
    .replace(/[^0-9a-fA-F]/g, "")
    .slice(0, 12)
    .toUpperCase()}`;
}

/** `dataPack id` (max 64): company + agenda + month. */
function packId(header: PohodaPackHeader, agenda: "fp" | "bnk"): string {
  const org = header.organizationId.replace(/[^0-9a-fA-F]/g, "").slice(0, 8);
  return `zettl-${org}-${agenda}-${header.period}`;
}

function packOpen(header: PohodaPackHeader, agenda: "fp" | "bnk", note: string): string {
  const ns = agenda === "fp" ? `xmlns:inv="${POHODA_NS.inv}"` : `xmlns:bnk="${POHODA_NS.bnk}"`;
  return (
    `<dat:dataPack version="2.0" id="${escapeXml(packId(header, agenda))}"` +
    ` ico="${escapeXml(normalizeIco(header.ico) ?? "")}"` +
    ` application="${APPLICATION}" note="${escapeXml(clip(note, 200))}"` +
    ` xmlns:dat="${POHODA_NS.dat}" ${ns} xmlns:typ="${POHODA_NS.typ}">`
  );
}

/** IČO as Pohoda compares it: digits only, max 15. Null when there is none. */
export function normalizeIco(ico: string | null | undefined): string | null {
  const digits = (ico ?? "").replace(/\D/g, "");
  return digits && digits.length <= 15 ? digits : null;
}

/** Why the export cannot run for this company, or null. */
export function pohodaExportBlocker(ico: string | null | undefined): "no_ico" | null {
  return normalizeIco(ico) ? null : "no_ico";
}

/** First and last day of a `YYYY-MM` month. */
export function monthBounds(period: string): { from: string; to: string } {
  const [y, m] = period.split("-").map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${period}-01`, to: `${period}-${String(last).padStart(2, "0")}` };
}

/** `pohoda-<company>-<YYYY-MM>.zip`, ASCII only. */
export function pohodaZipName(companyName: string, period: string): string {
  const slug =
    companyName
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-zA-Z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase()
      .slice(0, 40) || "firma";
  return `pohoda-${slug}-${period}.zip`;
}

// ---------------------------------------------------------------------------
// Received documents
// ---------------------------------------------------------------------------

type RateVat = "high" | "low" | "third" | "none";

/** Czech VAT rate (percent) to Pohoda's rate slot. 21 high; 12 (and 15 before 2024) low; 10 third. */
export function pohodaVatSlot(rate: number): RateVat | null {
  if (rate === 21) return "high";
  if (rate === 12 || rate === 15) return "low";
  if (rate === 10) return "third";
  if (rate === 0) return "none";
  return null;
}

function currencyOf(value: string | null | undefined): string {
  return (value || LOCAL_CURRENCY).trim().toUpperCase();
}

function supplierXml(s: PohodaSupplier | null | undefined): string {
  if (!s) return "";
  const parts = [
    el("typ:company", clip(s.name, 255)),
    el("typ:city", clip(s.city, 45)),
    el("typ:street", clip(s.street, 64)),
    el("typ:zip", clip(s.zip, 15)),
    el("typ:ico", normalizeIco(s.ico)),
    el("typ:dic", clip((s.dic ?? "").replace(/\s+/g, "").toUpperCase(), 18)),
  ].join("");
  return parts
    ? `<inv:partnerIdentity><typ:address>${parts}</typ:address></inv:partnerIdentity>`
    : "";
}

/** Usable VAT lines: known rate slot, finite numbers. Null when the breakdown cannot be used. */
export function pohodaVatLines(doc: PohodaReceivedDocument): PohodaVatLine[] | null {
  const lines = (doc.vat ?? []).filter(
    (l) => Number.isFinite(l.base) && Number.isFinite(l.vat) && (l.base !== 0 || l.vat !== 0)
  );
  if (lines.length === 0) return null;
  if (lines.some((l) => pohodaVatSlot(l.rate) == null)) return null;
  return lines;
}

function itemsXml(lines: PohodaVatLine[], foreign: boolean): string {
  const priceBlock = foreign ? "inv:foreignCurrency" : "inv:homeCurrency";
  const items = lines.map((l) => {
    const slot = pohodaVatSlot(l.rate)!;
    const text = slot === "none" ? "Plnění bez DPH" : `Plnění v sazbě ${l.rate} %`;
    return (
      `<inv:invoiceItem>` +
      el("inv:text", text) +
      `<inv:quantity>1</inv:quantity>` +
      `<inv:payVAT>false</inv:payVAT>` +
      `<inv:rateVAT>${slot}</inv:rateVAT>` +
      `<${priceBlock}>` +
      `<typ:unitPrice>${pohodaAmount(l.base)}</typ:unitPrice>` +
      `<typ:price>${pohodaAmount(l.base)}</typ:price>` +
      `<typ:priceVAT>${pohodaAmount(l.vat)}</typ:priceVAT>` +
      `</${priceBlock}>` +
      `</inv:invoiceItem>`
    );
  });
  return `<inv:invoiceDetail>${items.join("")}</inv:invoiceDetail>`;
}

function summaryXml(doc: PohodaReceivedDocument, hasItems: boolean): string {
  const currency = currencyOf(doc.currency);
  const foreign = currency !== LOCAL_CURRENCY;
  const total = doc.total ?? 0;
  let money: string;
  if (foreign) {
    money =
      `<inv:foreignCurrency>` +
      `<typ:currency><typ:ids>${escapeXml(clip(currency, 19))}</typ:ids></typ:currency>` +
      (doc.rate && doc.rate > 0 ? `<typ:rate>${rateText(doc.rate)}</typ:rate>` : "") +
      `<typ:amount>1</typ:amount>` +
      (hasItems ? "" : `<typ:priceSum>${pohodaAmount(total)}</typ:priceSum>`) +
      `</inv:foreignCurrency>`;
  } else {
    money = hasItems
      ? ""
      : `<inv:homeCurrency><typ:priceNone>${pohodaAmount(total)}</typ:priceNone></inv:homeCurrency>`;
  }
  return (
    `<inv:invoiceSummary>` +
    `<inv:roundingDocument>none</inv:roundingDocument>` +
    `<inv:roundingVAT>none</inv:roundingVAT>` +
    money +
    `</inv:invoiceSummary>`
  );
}

/** The `inv:invoice` for one received document. */
export function pohodaInvoiceXml(doc: PohodaReceivedDocument): string {
  const number = pohodaDocumentNumber(doc.id);
  const currency = currencyOf(doc.currency);
  const foreign = currency !== LOCAL_CURRENCY;
  const lines = pohodaVatLines(doc);
  const issue = isoDate(doc.issueDate);
  // Unknown dates are left out: Pohoda then fills its own defaults (DUZP = issue date).
  const tax = isoDate(doc.taxDate);
  const vendor = clip(doc.supplier?.name, 200);
  const original = clip(doc.documentNumber, 32);
  const text = clip(
    [vendor || "Přijatý doklad", original].filter(Boolean).join(" – ") +
      (doc.description ? ` (${doc.description})` : ""),
    240
  );

  const notes = [`Zettl ${doc.id}`];
  if (doc.attachmentPath) notes.push(`Soubor: ${doc.attachmentPath}`);
  if (!lines) notes.push("Rozpis DPH nebyl vytěžen, celá částka je bez sazby – zkontrolujte.");
  if (foreign && !(doc.rate && doc.rate > 0)) notes.push("Kurz nebyl k dispozici.");

  const header =
    `<inv:invoiceHeader>` +
    `<inv:invoiceType>receivedInvoice</inv:invoiceType>` +
    `<inv:number><typ:numberRequested>${number}</typ:numberRequested></inv:number>` +
    el("inv:symVar", symbol(doc.variableSymbol, 20)) +
    el("inv:originalDocument", original) +
    `<inv:symPar>${number}</inv:symPar>` +
    el("inv:date", issue) +
    el("inv:dateTax", tax) +
    el("inv:dateDue", isoDate(doc.dueDate)) +
    el("inv:text", text) +
    supplierXml(doc.supplier) +
    el("inv:intNote", notes.join("\n")) +
    `</inv:invoiceHeader>`;

  const attachments = doc.attachmentPath
    ? `<inv:attachments><typ:shortcut><typ:target>${escapeXml(
        clip(doc.attachmentPath, 255)
      )}</typ:target></typ:shortcut></inv:attachments>`
    : "";

  return (
    `<inv:invoice version="2.0">` +
    header +
    (lines ? itemsXml(lines, foreign) : "") +
    summaryXml(doc, !!lines) +
    attachments +
    `</inv:invoice>`
  );
}

export function buildPohodaInvoicesXml(
  header: PohodaPackHeader,
  documents: PohodaReceivedDocument[]
): string {
  const items = documents.map(
    (doc) =>
      `<dat:dataPackItem version="2.0" id="R-${escapeXml(doc.id)}">${pohodaInvoiceXml(doc)}</dat:dataPackItem>`
  );
  return (
    `<?xml version="1.0" encoding="Windows-1250"?>\n` +
    packOpen(header, "fp", `Zettl – přijaté doklady ${header.period}`) +
    items.join("\n") +
    `</dat:dataPack>\n`
  );
}

// ---------------------------------------------------------------------------
// Bank movements
// ---------------------------------------------------------------------------

/** The `bnk:bank` for one movement. `account` is the Pohoda bank account abbreviation. */
export function pohodaBankXml(m: PohodaBankMovement, account?: string | null): string {
  const currency = currencyOf(m.currency);
  const foreign = currency !== LOCAL_CURRENCY;
  const price = pohodaAmount(Math.abs(m.amount));
  const matched = Array.from(new Set(m.matchedDocumentIds ?? []));
  const counterAccount = clip(m.counterAccount, 34);
  const counterBank = clip(m.counterBankCode, 11);
  const text = clip(m.description || m.counterName || "Bankovní pohyb", 96);

  const notes = [`Zettl ${m.id}`];
  if (matched.length > 1) {
    notes.push(`Spárováno s doklady: ${matched.map(pohodaDocumentNumber).join(", ")}`);
  }

  const name = clip(m.counterName, 255);
  const header =
    `<bnk:bankHeader>` +
    `<bnk:bankType>${m.amount < 0 ? "expense" : "receipt"}</bnk:bankType>` +
    (account
      ? `<bnk:account><typ:ids>${escapeXml(clip(account, 19))}</typ:ids></bnk:account>`
      : "") +
    el("bnk:symVar", symbol(m.variableSymbol, 20)) +
    el("bnk:datePayment", isoDate(m.date)) +
    el("bnk:text", text) +
    (name
      ? `<bnk:partnerIdentity><typ:address><typ:company>${escapeXml(name)}</typ:company></typ:address></bnk:partnerIdentity>`
      : "") +
    (counterAccount && counterBank
      ? `<bnk:paymentAccount><typ:accountNo>${escapeXml(counterAccount)}</typ:accountNo><typ:bankCode>${escapeXml(counterBank)}</typ:bankCode></bnk:paymentAccount>`
      : "") +
    el("bnk:symConst", symbol(m.constantSymbol, 4)) +
    el("bnk:symSpec", symbol(m.specificSymbol, 16)) +
    (matched.length === 1 ? `<bnk:symPar>${pohodaDocumentNumber(matched[0])}</bnk:symPar>` : "") +
    el("bnk:intNote", notes.join("\n")) +
    `</bnk:bankHeader>`;

  const money = foreign
    ? `<bnk:foreignCurrency><typ:currency><typ:ids>${escapeXml(
        clip(currency, 19)
      )}</typ:ids></typ:currency><typ:priceSum>${price}</typ:priceSum></bnk:foreignCurrency>`
    : `<bnk:homeCurrency><typ:priceNone>${price}</typ:priceNone></bnk:homeCurrency>`;

  return (
    `<bnk:bank version="2.0">` +
    header +
    `<bnk:bankSummary><bnk:roundingDocument>none</bnk:roundingDocument><bnk:roundingVAT>none</bnk:roundingVAT>${money}</bnk:bankSummary>` +
    `</bnk:bank>`
  );
}

export function buildPohodaBankXml(
  header: PohodaPackHeader,
  movements: PohodaBankMovement[],
  account?: string | null
): string {
  const items = movements.map(
    (m) =>
      `<dat:dataPackItem version="2.0" id="B-${escapeXml(m.id)}">${pohodaBankXml(m, account)}</dat:dataPackItem>`
  );
  return (
    `<?xml version="1.0" encoding="Windows-1250"?>\n` +
    packOpen(header, "bnk", `Zettl – bankovní pohyby ${header.period}`) +
    items.join("\n") +
    `</dat:dataPack>\n`
  );
}

// ---------------------------------------------------------------------------
// Windows-1250
// ---------------------------------------------------------------------------

let cp1250: Map<number, number> | null = null;

/** Unicode code point → Windows-1250 byte, read from the platform's own decoder. */
function cp1250Table(): Map<number, number> {
  if (cp1250) return cp1250;
  const table = new Map<number, number>();
  const decoder = new TextDecoder("windows-1250");
  for (let byte = 0x80; byte <= 0xff; byte++) {
    const ch = decoder.decode(new Uint8Array([byte]));
    const cp = ch.codePointAt(0)!;
    if (ch.length === 1 && cp !== 0xfffd) table.set(cp, byte);
  }
  cp1250 = table;
  return table;
}

/** True when every character of `value` exists in Windows-1250 (no references needed). */
export function isWindows1250Encodable(value: string): boolean {
  const table = cp1250Table();
  for (const ch of value) {
    const cp = ch.codePointAt(0)!;
    if (cp >= 0x80 && !table.has(cp)) return false;
  }
  return true;
}

/**
 * The XML as Windows-1250 bytes, the encoding every Stormware sample declares.
 * A character the code page lacks (emoji, Cyrillic, …) becomes a numeric character
 * reference, which any XML parser reads back as the same character. Only valid for
 * XML whose non-ASCII characters sit in text or attribute values, as ours do.
 */
export function encodeWindows1250(xml: string): Uint8Array {
  const table = cp1250Table();
  const out: number[] = [];
  for (const ch of xml) {
    const cp = ch.codePointAt(0)!;
    if (cp < 0x80) out.push(cp);
    else if (table.has(cp)) out.push(table.get(cp)!);
    else for (const c of `&#${cp};`) out.push(c.charCodeAt(0));
  }
  return Uint8Array.from(out);
}

// ---------------------------------------------------------------------------
// Zettl rows → builder input (shared by the ZIP export and the mServer send)
// ---------------------------------------------------------------------------

export interface PohodaReceiptRow {
  id: string;
  vendor: string | null;
  invoice_number: string | null;
  issue_date: string | null;
  amount: number | string | null;
  currency: string | null;
  variable_symbol: string | null;
  description: string | null;
  file_url: string | null;
  transaction_id: string | null;
}

export interface PohodaTransactionRow {
  id: string;
  date: string | null;
  amount: number | string | null;
  currency: string | null;
  counter_account: string | null;
  counter_bank_code: string | null;
  counter_name: string | null;
  variable_symbol: string | null;
  description: string | null;
  receipt_id: string | null;
}

export const POHODA_RECEIPT_COLUMNS =
  "id, vendor, invoice_number, issue_date, amount, currency, variable_symbol, description, file_url, transaction_id";
export const POHODA_TRANSACTION_COLUMNS =
  "id, date, amount, currency, counter_account, counter_bank_code, counter_name, variable_symbol, description, receipt_id";

/**
 * A receipt as a received document. Zettl does not store a VAT breakdown, IČO/DIČ,
 * DUZP or due date for receipts yet, so those stay empty. The amount keeps its sign:
 * a refund stays negative.
 */
export function receiptToPohoda(
  r: PohodaReceiptRow,
  extra: { rate: number | null; attachmentPath: string | null }
): PohodaReceivedDocument {
  return {
    id: r.id,
    documentNumber: r.invoice_number,
    supplier: r.vendor ? { name: r.vendor } : null,
    issueDate: r.issue_date,
    currency: currencyOf(r.currency),
    rate: extra.rate,
    total: r.amount == null ? null : Number(r.amount),
    vat: null,
    variableSymbol: r.variable_symbol,
    description: r.description,
    attachmentPath: extra.attachmentPath,
  };
}

/** A transaction as a bank movement; null for one without a date or a zero amount. */
export function transactionToPohoda(
  t: PohodaTransactionRow,
  matchedReceiptIds: Iterable<string>
): PohodaBankMovement | null {
  if (!t.date || t.amount == null || Number(t.amount) === 0) return null;
  const matched = new Set(matchedReceiptIds);
  if (t.receipt_id) matched.add(t.receipt_id);
  return {
    id: t.id,
    date: t.date,
    amount: Number(t.amount),
    currency: t.currency,
    counterAccount: t.counter_account,
    counterBankCode: t.counter_bank_code,
    counterName: t.counter_name,
    variableSymbol: t.variable_symbol,
    description: t.description,
    matchedDocumentIds: Array.from(matched),
  };
}

/** transaction id → receipt ids, from `receipts.transaction_id` (the reliable link). */
export function receiptsByTransaction(
  links: { id: string; transaction_id: string | null }[]
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const r of links) {
    if (!r.transaction_id) continue;
    if (!map.has(r.transaction_id)) map.set(r.transaction_id, new Set());
    map.get(r.transaction_id)!.add(r.id);
  }
  return map;
}
